import { ref, computed, watch } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { dataHojeBR, componentesBrasil } from '@/utils/useDataBR'
import {
  SEEDS_CATALOG,
  GRUPO_MASTER,
  GRUPO_ORGANIZACAO,
  ESCOPO_TENANT,
  ESCOPO_MASTER,
  ESCOPO_ORGANIZACAO,
  TOKEN_EMPRESA,
  TOKEN_ORGANIZACAO,
  TOKEN_FORCAR_DOC,
  CORPO_EMPRESAS,
  CORPO_EMPRESAS_OU_TODAS,
  CORPO_SINCRONIZAR_LISTAS,
  CORPO_TRIBUTO_CONFIGURACAO,
  obterSeed,
  rotuloStatus
} from '@/constants/seedsCatalog'

/**
 * Composable central da "Central de Seeds".
 *
 * Responsabilidades:
 *  - Expor os seeds do grupo ativo e o progresso (concluídos / total).
 *  - Derivar o status dos seeds TENANT a partir dos endpoints de status existentes.
 *  - Executar seeds/exportações/reconciliações/verificações de forma declarativa.
 *  - Manter relatórios em memória (FASE 1 — sem persistência no backend).
 *  - Reagir à troca de empresa ativa (authStore.empresaAtiva).
 */
export function useSeeds () {
  const authStore = useAuthStore()

  const empresaAtiva = computed(() => authStore.empresaAtiva)
  // Organização ativa (multi-organização) — escopo de dado dentro do banco Tenant
  const organizacaoAtiva = computed(() => authStore.organizacaoAtiva)

  const grupoAtivo = ref(GRUPO_MASTER)
  const statusSeeds = ref({})
  const execucoesPersistidas = ref([])
  const loteSelecionadoId = ref(null)
  const carregandoStatus = ref(false)

  // Filtros da timeline (período + somente erros). Vivem no composable para que
  // a lista visível e o relatório selecionado permaneçam sempre consistentes.
  // O período padrão é "Hoje": ao abrir a Central de Seeds, a timeline exibe
  // apenas as execuções do dia atual (a última execução fica pré-selecionada).
  function dataISO (iso) {
    const c = componentesBrasil(iso)
    if (!c) return null
    return `${c.ano}-${String(c.mes).padStart(2, '0')}-${String(c.dia).padStart(2, '0')}`
  }

  const filtroSomenteErros = ref(false)
  const filtroDataInicio = ref(dataHojeBR())
  const filtroDataFim = ref(dataHojeBR())

  function aplicarFiltroHoje () {
    const hoje = dataHojeBR()
    filtroDataInicio.value = hoje
    filtroDataFim.value = hoje
  }

  // Configurações por seed (controladas pela UI via modal de engrenagem)
  const configuracoesSeeds = ref({})

  function inicializarConfiguracoes () {
    const out = {}
    for (const seed of SEEDS_CATALOG) {
      if (Array.isArray(seed.camposConfig) && seed.camposConfig.length > 0) {
        const cfg = {}
        for (const campo of seed.camposConfig) cfg[campo.chave] = campo.padrao
        out[seed.chave] = cfg
      }
    }
    configuracoesSeeds.value = out
  }

  function seedTemConfig (seed) {
    return Array.isArray(seed?.camposConfig) && seed.camposConfig.length > 0
  }

  function obterConfig (seed) {
    return configuracoesSeeds.value[seed.chave] || {}
  }

  function salvarConfig (seed, valores) {
    configuracoesSeeds.value = {
      ...configuracoesSeeds.value,
      [seed.chave]: { ...valores }
    }
  }

  // ============================ Resolução declarativa ============================

  function resolverValorToken (valor, seed) {
    if (valor === TOKEN_EMPRESA) return empresaAtiva.value
    if (valor === TOKEN_ORGANIZACAO) return organizacaoAtiva.value
    if (valor === TOKEN_FORCAR_DOC) return obterConfig(seed).forcarAtualizacao ?? false
    return valor
  }

  function resolverParams (seed, tipo) {
    const decl = seed.endpoints?.[tipo]?.params
    if (!decl) return undefined
    const out = {}
    for (const [k, v] of Object.entries(decl)) out[k] = resolverValorToken(v, seed)
    return out
  }

  function resolverCorpo (seed, tipo = 'executar') {
    const corpo = seed.endpoints?.[tipo]?.corpo
    if (!corpo) return undefined

    if (corpo === CORPO_EMPRESAS) {
      return {
        todasEmpresas: false,
        empresasIds: empresaAtiva.value ? [empresaAtiva.value] : [],
        idOrganizacao: organizacaoAtiva.value
      }
    }
    if (corpo === CORPO_EMPRESAS_OU_TODAS) {
      return {
        todasEmpresas: !empresaAtiva.value,
        empresasIds: empresaAtiva.value ? [empresaAtiva.value] : [],
        idOrganizacao: organizacaoAtiva.value
      }
    }
    if (corpo === CORPO_SINCRONIZAR_LISTAS) {
      const cfg = obterConfig(seed)
      return {
        todasEmpresas: false,
        empresasIds: empresaAtiva.value ? [empresaAtiva.value] : [],
        idOrganizacao: organizacaoAtiva.value,
        atualizarTextos: cfg.atualizarTextos ?? true,
        atualizarStatus: cfg.atualizarStatus ?? false
      }
    }
    if (corpo === CORPO_TRIBUTO_CONFIGURACAO) {
      const cfg = obterConfig(seed)
      return {
        idEmpresa: empresaAtiva.value,
        idOrganizacao: organizacaoAtiva.value,
        fase: cfg.fase,
        dataInicioVigencia: cfg.dataInicioVigencia || null,
        dataFimVigencia: cfg.dataFimVigencia || null
      }
    }
    return undefined
  }

  // ============================ Status dos seeds ============================

  async function carregarStatusSeed (seed) {
    if (!seed.endpoints?.status) return
    try {
      const params = resolverParams(seed, 'status')
      const config = params && Object.keys(params).length > 0 ? { params } : {}
      const response = await api.get(seed.endpoints.status.url, config)
      const campo = seed.endpoints.status.campoChave
      const existe = campo ? response.data?.[campo] === true : false
      statusSeeds.value = { ...statusSeeds.value, [seed.chave]: existe ? 'concluido' : 'pendente' }
    } catch (error) {
      statusSeeds.value = { ...statusSeeds.value, [seed.chave]: 'erro' }
    }
  }

  async function carregarStatus () {
    carregandoStatus.value = true
    const empresa = empresaAtiva.value
    const organizacao = organizacaoAtiva.value
    const novo = { ...statusSeeds.value }

    for (const seed of SEEDS_CATALOG) {
      if (seed.escopo === ESCOPO_TENANT || seed.escopo === ESCOPO_ORGANIZACAO) {
        // Seeds de organização exigem a organização ativa; os de tenant exigem a empresa.
        const contextoDisponivel = seed.escopo === ESCOPO_ORGANIZACAO ? organizacao : empresa
        if (!contextoDisponivel) {
          novo[seed.chave] = 'pendente'
          continue
        }
        if (seed.endpoints?.status) {
          try {
            const params = resolverParams(seed, 'status')
            const config = params && Object.keys(params).length > 0 ? { params } : {}
            const response = await api.get(seed.endpoints.status.url, config)
            const campo = seed.endpoints.status.campoChave
            novo[seed.chave] = response.data?.[campo] === true ? 'concluido' : 'pendente'
          } catch (error) {
            novo[seed.chave] = 'erro'
          }
        } else if (!novo[seed.chave]) {
          novo[seed.chave] = 'nao_rastreado'
        }
      } else if (!novo[seed.chave]) {
        // MASTER: não rastreado nesta fase (até ser executado na sessão)
        novo[seed.chave] = 'nao_rastreado'
      }
    }

    statusSeeds.value = novo
    carregandoStatus.value = false
  }

  // ============================ Relatórios ============================

  function extrairMensagemErro (error) {
    const data = error?.response?.data
    // GUARDIAO: um corpo vazio ('') nao pode ser tratado como mensagem valida,
    // senao o erro eh classificado como sucesso (contradicao relatorio x status).
    if (typeof data === 'string' && data.trim()) return data.trim()
    if (data?.message) return data.message
    if (data?.title) return data.title
    if (data?.errors) return Object.values(data.errors).flat().join('; ')
    return error?.message || 'Erro ao executar a operação.'
  }

  function classificar (data, erroMsg) {
    if (erroMsg) return 'erro'
    const detalhes = data?.detalhes || []
    const texto = [data?.message, ...detalhes].filter(Boolean).join(' ').toUpperCase()
    if (texto.includes('ERRO') || texto.includes('FALHA')) return 'aviso'
    return 'sucesso'
  }

  function registrarRelatorio (seed, tipo, data, erroMsg) {
    const detalhes = erroMsg ? [erroMsg] : (data?.detalhes || [])
    const titulo = erroMsg || data?.message || 'Operação concluída.'
    const status = classificar(data, erroMsg)

    const relatorio = {
      id: `${seed.chave}-${tipo}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      chave: seed.chave,
      nomeSeed: seed.nome,
      icone: seed.icone,
      escopo: seed.escopo,
      grupo: seed.grupo,
      versao: seed.versao,
      tipo,
      status,
      titulo,
      detalhes,
      empresaId: (seed.escopo === ESCOPO_TENANT || seed.escopo === ESCOPO_ORGANIZACAO) ? empresaAtiva.value : null,
      organizacaoId: seed.escopo === ESCOPO_ORGANIZACAO ? organizacaoAtiva.value : null,
      dataHora: new Date().toISOString()
    }

    return relatorio
  }

  // ============================ Histórico persistido (SeedExecucao) ============================

  function mapearExecucaoPersistida (e) {
    const seed = obterSeed(e.chaveSeed)
    const detalhes = e.detalhes || []
    const primeiroDetalhe = detalhes.length > 0 ? String(detalhes[0]) : null

    let titulo
    if (e.status === 'Erro') {
      titulo = e.mensagemErro || primeiroDetalhe || 'Erro na execução'
    } else {
      // O título reflete o conteúdo real do detalhe persistido (ex.: "Empresa 3 (...): ...").
      titulo = primeiroDetalhe || 'Execução concluída'
    }

    return {
      id: `persist-${e.idSeedExecucao}`,
      idLoteExecucao: e.idLoteExecucao || null,
      chave: e.chaveSeed,
      nomeSeed: e.nomeSeed,
      icone: seed?.icone || 'bi-clock-history',
      escopo: seed?.escopo || null,
      grupo: seed?.grupo || null,
      versao: seed?.versao || null,
      tipo: 'executar',
      status: e.status === 'Erro' ? 'erro' : 'sucesso',
      titulo,
      detalhes,
      empresaId: e.idEmpresa,
      dataHora: e.tsExecucao,
      persistido: true,
      idUsuario: e.idUsuario,
      nomeUsuario: e.nomeUsuario
    }
  }

  async function carregarExecucoesPersistidas () {
    try {
      // Não envia empresaId: o backend (restrito a admin interno) retorna o histórico
      // completo, permitindo exibir os badges de seeds multi-tenant (ex.: MIGRATIONS).
      const params = { pageSize: 200 }

      const response = await api.get('/Setup/execucoes-seed', {
        params,
        _suppressLoading: true
      })
      execucoesPersistidas.value = (response.data || []).map(mapearExecucaoPersistida)
    } catch (error) {
      console.error('Falha ao carregar execuções persistidas:', error)
      execucoesPersistidas.value = []
    }
  }

  // ============================ Execução ============================

  async function executarSeed (seed, tipo = 'executar') {
    const endpoint = seed.endpoints?.[tipo]
    if (!endpoint) return null

    if (tipo === 'executar') {
      statusSeeds.value = { ...statusSeeds.value, [seed.chave]: 'executando' }
    }

    // Snapshot dos relatórios já existentes para identificar, ao final, os recém-criados.
    const idsAnteriores = new Set(execucoesPersistidas.value.map(r => r.id))

    const metodo = (endpoint.metodo || 'post').toLowerCase()
    const params = resolverParams(seed, tipo)
    const config = {}
    if (params && Object.keys(params).length > 0) config.params = params

    try {
      let response
      if (metodo === 'get') {
        response = await api.get(endpoint.url, config)
      } else {
        const corpo = resolverCorpo(seed, tipo)
        response = await api.post(endpoint.url, corpo === undefined ? null : corpo, config)
      }

      const relatorio = registrarRelatorio(seed, tipo, response.data, null)

      if (tipo === 'executar') {
        statusSeeds.value = { ...statusSeeds.value, [seed.chave]: 'concluido' }
        if (seed.endpoints?.status) await carregarStatusSeed(seed)
        await carregarExecucoesPersistidas()
        selecionarUltimaExecucao(seed.chave, idsAnteriores)
      }

      return { sucesso: true, relatorio, data: response.data }
    } catch (error) {
      const mensagem = extrairMensagemErro(error)
      const relatorio = registrarRelatorio(seed, tipo, null, mensagem)

      if (tipo === 'executar') {
        statusSeeds.value = { ...statusSeeds.value, [seed.chave]: 'erro' }
        await carregarExecucoesPersistidas()
        selecionarUltimaExecucao(seed.chave, idsAnteriores)
      }

      return { sucesso: false, relatorio, data: null }
    }
  }

  // ============================ Bloqueios e progresso ============================

  function seedDesabilitado (seed) {
    if (statusSeeds.value[seed.chave] === 'executando') return true
    // Seeds de organização exigem empresa + organização (o escopo é dentro do banco do tenant).
    if (seed.escopo === ESCOPO_ORGANIZACAO) {
      if (!empresaAtiva.value || !organizacaoAtiva.value) return true
    } else if (seed.escopo === ESCOPO_TENANT && !empresaAtiva.value) {
      return true
    }
    if (seed.preRequisito && statusSeeds.value[seed.preRequisito] !== 'concluido') return true
    return false
  }

  function motivoBloqueio (seed) {
    if (statusSeeds.value[seed.chave] === 'executando') return 'Executando...'
    if (seed.escopo === ESCOPO_ORGANIZACAO) {
      if (!empresaAtiva.value) return 'Selecione uma empresa no topo'
      if (!organizacaoAtiva.value) return 'Selecione uma organização no topo'
    } else if (seed.escopo === ESCOPO_TENANT && !empresaAtiva.value) {
      return 'Selecione uma empresa no topo'
    }
    if (seed.preRequisito && statusSeeds.value[seed.preRequisito] !== 'concluido') {
      const pre = SEEDS_CATALOG.find(s => s.chave === seed.preRequisito)
      return `Pré-requisito pendente: ${pre?.nome || seed.preRequisito}`
    }
    return ''
  }

  // ============================ Computeds ============================

  const seedsDoGrupoAtual = computed(() =>
    SEEDS_CATALOG.filter(s => s.grupo === grupoAtivo.value)
  )

  const progresso = computed(() => {
    const lista = seedsDoGrupoAtual.value
    const total = lista.length
    const concluidos = lista.filter(s => statusSeeds.value[s.chave] === 'concluido').length
    const percentual = total === 0 ? 0 : Math.round((concluidos / total) * 100)
    return { total, concluidos, percentual }
  })

  // Timeline: expõe APENAS o histórico persistido (SeedExecucao), um badge por
  // registro (por empresa). O relatório em memória é usado apenas para o toast
  // imediato e NÃO compõe a timeline. Sem empresaId, o backend devolve TODOS os
  // registros (globais + todas as empresas), garantindo os badges multi-tenant
  // (ex.: MIGRATIONS) e mantendo os globais (Master) sempre visíveis.
  // Aplica os filtros da timeline (período + somente erros) para que a lista
  // visível e o relatório selecionado permaneçam consistentes.
  const relatoriosVisiveis = computed(() => {
    let lista = [...execucoesPersistidas.value]

    if (filtroSomenteErros.value) {
      lista = lista.filter(r => r.status === 'erro')
    }

    if (filtroDataInicio.value && filtroDataFim.value) {
      lista = lista.filter(r => {
        const iso = dataISO(r.dataHora)
        if (!iso) return false
        return iso >= filtroDataInicio.value && iso <= filtroDataFim.value
      })
    }

    return lista.sort((a, b) => new Date(b.dataHora) - new Date(a.dataHora))
  })

  // Agrupa os registros persistidos por lote de execução (IdLoteExecucao).
  // Um clique de seed gera N registros (um por empresa) que compartilham o mesmo
  // lote; a timeline exibe um badge por lote e o detalhe lista os itens por empresa.
  // Registros legados (sem lote) caem em um grupo próprio (fallback: id do registro).
  const lotesVisiveis = computed(() => {
    const grupos = new Map()

    for (const r of relatoriosVisiveis.value) {
      const chaveLote = r.idLoteExecucao || r.id
      let lote = grupos.get(chaveLote)
      if (!lote) {
        lote = {
          id: `lote-${chaveLote}`,
          idLote: chaveLote,
          chave: r.chave,
          nomeSeed: r.nomeSeed,
          icone: r.icone,
          escopo: r.escopo,
          grupo: r.grupo,
          versao: r.versao,
          dataHora: r.dataHora,
          itens: []
        }
        grupos.set(chaveLote, lote)
      }
      lote.itens.push(r)
      if (new Date(r.dataHora) > new Date(lote.dataHora)) lote.dataHora = r.dataHora
    }

    const lista = [...grupos.values()]

    for (const lote of lista) {
      lote.itens.sort((a, b) => new Date(a.dataHora) - new Date(b.dataHora))
      lote.status = lote.itens.some(i => i.status === 'erro')
        ? 'erro'
        : (lote.itens.some(i => i.status === 'aviso') ? 'aviso' : 'sucesso')
      lote.titulo = lote.itens.length === 1
        ? lote.itens[0].titulo
        : `${lote.itens.length} empresas processadas`
    }

    return lista.sort((a, b) => new Date(b.dataHora) - new Date(a.dataHora))
  })

  const loteSelecionado = computed(() => {
    const lista = lotesVisiveis.value
    if (lista.length === 0) return null
    return lista.find(l => l.id === loteSelecionadoId.value) || lista[0]
  })

  function selecionarLote (id) {
    loteSelecionadoId.value = id
  }

  // Seleciona o lote da execução mais recente (opcionalmente de um seed específico),
  // para que, logo após executar, o painel de detalhe exiba o resultado recém-gerado.
  // 'idsAnteriores' restringe a seleção aos lotes que contêm registros criados pela
  // execução atual, evitando selecionar um lote antigo quando nada novo foi persistido.
  function selecionarUltimaExecucao (chave, idsAnteriores) {
    const lista = lotesVisiveis.value
    if (lista.length === 0) return

    let candidatos = chave ? lista.filter(l => l.chave === chave) : lista
    if (idsAnteriores) {
      candidatos = candidatos.filter(l => l.itens.some(r => !idsAnteriores.has(r.id)))
    }

    if (candidatos.length > 0) loteSelecionadoId.value = candidatos[0].id
  }

  function definirGrupo (grupo) {
    grupoAtivo.value = grupo
  }

  // ============================ Ciclo de vida ============================

  inicializarConfiguracoes()

  // Mantém o lote selecionado válido: se o id atual não existir na timeline,
  // seleciona o primeiro lote visível.
  watch(lotesVisiveis, (lista) => {
    if (lista.length === 0) {
      loteSelecionadoId.value = null
      return
    }
    if (!lista.some(l => l.id === loteSelecionadoId.value)) {
      loteSelecionadoId.value = lista[0].id
    }
  }, { immediate: true })

  // immediate: garante a carga inicial (status + histórico) mesmo sem troca de empresa
  watch(empresaAtiva, () => {
    carregarStatus()
    carregarExecucoesPersistidas()
  }, { immediate: true })

  // Reage à troca de organização ativa (multi-organização)
  watch(organizacaoAtiva, () => {
    carregarStatus()
  })

  return {
    // contexto
    empresaAtiva,
    organizacaoAtiva,
    // grupo (eixo de navegação)
    grupoAtivo,
    definirGrupo,
    // dados
    seedsDoGrupoAtual,
    statusSeeds,
    progresso,
    carregandoStatus,
    // relatórios
    execucoesPersistidas,
    relatoriosVisiveis,
    lotesVisiveis,
    loteSelecionado,
    loteSelecionadoId,
    selecionarLote,
    carregarExecucoesPersistidas,
    // filtros da timeline
    filtroSomenteErros,
    filtroDataInicio,
    filtroDataFim,
    aplicarFiltroHoje,
    // configurações por seed
    configuracoesSeeds,
    obterConfig,
    salvarConfig,
    seedTemConfig,
    // ações
    carregarStatus,
    executarSeed,
    // helpers
    seedDesabilitado,
    motivoBloqueio,
    rotuloStatus,
    // constantes reexportadas por conveniência da UI
    GRUPO_MASTER,
    GRUPO_ORGANIZACAO,
    ESCOPO_TENANT,
    ESCOPO_MASTER,
    ESCOPO_ORGANIZACAO
  }
}
